/**
 * Knowledge base file API — same-origin `/api` proxy in dev (webpack.config.js).
 * Backend returns snake_case; we normalize to camelCase for the task pane.
 * List query uses `page`, `per_page` (snake_case page size), `search` — backend expects snake_case for limit.
 */

import { apiRequest, fetchApi, getStoredAccessToken } from "./client";
import { isTokenExpired } from "../utils/jwt";
import type {
  KnowledgeBase,
  KnowledgeBaseFile,
  KnowledgeBaseFileDto,
  KnowledgeBaseFileStatus,
  KnowledgeBaseFilesListResponse,
  KnowledgeBaseFilesPagination,
} from "../types/knowledgeBase";

const KB_FILES_PATH = "/knowledge-base/files";
const KB_FILES_UPLOAD_PATH = "/knowledge-base/add_in/files";
const KB_DEFAULT_PATH = "/knowledge-base/default";

const STATUS_SET = new Set<KnowledgeBaseFileStatus>(["pending", "processing", "completed", "failed"]);

function requireAccessToken(): string {
  const t = getStoredAccessToken();
  if (!t || isTokenExpired(t)) {
    throw new Error("Not signed in or session expired. Sign in from the add-in task pane.");
  }
  return t;
}

async function readErrorBody(response: Response): Promise<string> {
  try {
    const text = await response.text();
    if (!text.trim()) {
      return `Request failed (${response.status})`;
    }
    try {
      const j = JSON.parse(text) as Record<string, unknown>;
      const detail = j.detail ?? j.message ?? j.error;
      if (typeof detail === "string") {
        return detail;
      }
    } catch {
      return text;
    }
    return text;
  } catch {
    return `Request failed (${response.status})`;
  }
}

/** Maps default KB JSON (snake_case) to {@link KnowledgeBase}. */
export function knowledgeBaseFromDto(raw: unknown): KnowledgeBase {
  const r = raw as Record<string, unknown>;
  return {
    id: typeof r.id === "string" ? r.id : "",
    orgId: typeof r.org_id === "string" ? r.org_id : typeof r.orgId === "string" ? r.orgId : "",
    name: typeof r.name === "string" ? r.name : "",
    createdAt: typeof r.created_at === "string" ? r.created_at : typeof r.createdAt === "string" ? r.createdAt : "",
  };
}

/**
 * Org default knowledge base (used when creating sessions / add-in bootstrap).
 * Fails if the user has no default KB configured — callers should handle errors.
 */
export async function fetchDefaultKnowledgeBase(): Promise<KnowledgeBase> {
  const raw = await apiRequest<unknown>(KB_DEFAULT_PATH, { method: "GET" });
  return knowledgeBaseFromDto(raw);
}

function coerceStatus(raw: unknown): KnowledgeBaseFileStatus {
  if (typeof raw === "string" && STATUS_SET.has(raw as KnowledgeBaseFileStatus)) {
    return raw as KnowledgeBaseFileStatus;
  }
  return "pending";
}

/** Maps one backend file object (snake_case) to {@link KnowledgeBaseFile}. */
export function knowledgeBaseFileFromDto(raw: unknown): KnowledgeBaseFile {
  const r = raw as Partial<KnowledgeBaseFileDto> & Record<string, unknown>;

  const tagsRaw = r.tags;
  const tags = Array.isArray(tagsRaw)
    ? tagsRaw.filter((t): t is string => typeof t === "string")
    : [];

  return {
    id: typeof r.id === "string" ? r.id : "",
    kbId: typeof r.kb_id === "string" ? r.kb_id : "",
    fileName: typeof r.file_name === "string" ? r.file_name : "",
    s3Key: typeof r.s3_key === "string" ? r.s3_key : "",
    mimeType: r.mime_type === null || r.mime_type === undefined ? null : String(r.mime_type),
    status: coerceStatus(r.status),
    source: r.source === null || r.source === undefined ? null : String(r.source),
    uploadedAt: typeof r.uploaded_at === "string" ? r.uploaded_at : "",
    tags,
  };
}

/** Matches client list request page size; used when API omits total_pages. */
const KB_LIST_PAGE_SIZE_FALLBACK = 5;

function paginationFromDto(raw: unknown): KnowledgeBaseFilesPagination {
  const p = raw as Record<string, unknown>;
  const num = (v: unknown, fallback: number): number =>
    typeof v === "number" && !Number.isNaN(v) ? v : Number(v) || fallback;

  const totalItems = num(p.total_items, 0);
  const perPageVal = num(p.per_page, KB_LIST_PAGE_SIZE_FALLBACK);
  let totalPages = num(p.total_pages, 0);
  if (totalPages <= 0 && totalItems > 0) {
    const divisor = Math.max(1, perPageVal || KB_LIST_PAGE_SIZE_FALLBACK);
    totalPages = Math.max(1, Math.ceil(totalItems / divisor));
  }

  return {
    page: num(p.page, 1),
    perPage: perPageVal || KB_LIST_PAGE_SIZE_FALLBACK,
    totalItems,
    totalPages: totalPages > 0 ? totalPages : 0,
    hasNext: Boolean(p.has_next),
    hasPrevious: Boolean(p.has_previous),
  };
}

function knowledgeBaseFilesListFromPayload(raw: unknown): KnowledgeBaseFilesListResponse {
  if (raw === null || raw === undefined) {
    return {
      files: [],
      pagination: {
        page: 1,
        perPage: 5,
        totalItems: 0,
        totalPages: 0,
        hasNext: false,
        hasPrevious: false,
      },
    };
  }

  if (Array.isArray(raw)) {
    const files = raw.map((item) => knowledgeBaseFileFromDto(item));
    return {
      files,
      pagination: {
        page: 1,
        perPage: files.length,
        totalItems: files.length,
        totalPages: 1,
        hasNext: false,
        hasPrevious: false,
      },
    };
  }

  const r = raw as Record<string, unknown>;
  const filesRaw = r.files;
  const files = Array.isArray(filesRaw) ? filesRaw.map((item) => knowledgeBaseFileFromDto(item)) : [];
  const pagination = paginationFromDto(r.pagination);

  return { files, pagination };
}

export type ListKnowledgeBaseFilesParams = {
  page: number;
  perPage: number;
  /** Case-insensitive substring on file name (optional). */
  search?: string | null;
};

/**
 * GET list.
 */
export async function listKnowledgeBaseFilesPage(
  params: ListKnowledgeBaseFilesParams
): Promise<KnowledgeBaseFilesListResponse> {
  const { page, perPage, search } = params;
  const sp = new URLSearchParams();
  const safePage = Math.max(1, page);
  const safePer = Math.max(1, Math.min(100, perPage));
  sp.set("page", String(safePage));
  sp.set("per_page", String(safePer));
  const q = typeof search === "string" ? search.trim() : "";
  if (q.length > 0) {
    sp.set("search", q);
  }

  const url = `${KB_FILES_PATH}?${sp.toString()}`;
  const raw = await apiRequest<unknown>(url, { method: "GET" });
  return knowledgeBaseFilesListFromPayload(raw);
}

export async function uploadKnowledgeBaseFile(file: File): Promise<KnowledgeBaseFile> {
  const token = requireAccessToken();
  const form = new FormData();
  form.append("file", file);

  const response = await fetchApi(KB_FILES_UPLOAD_PATH, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${token}`,
    },
    body: form,
  });

  if (!response.ok) {
    throw new Error(await readErrorBody(response));
  }
  const json: unknown = await response.json();
  return knowledgeBaseFileFromDto(json);
}

export async function deleteKnowledgeBaseFile(fileId: string): Promise<void> {
  await apiRequest<void>(`${KB_FILES_PATH}/${encodeURIComponent(fileId)}`, {
    method: "DELETE",
  });
}
